// ShiftPolicyViewPage.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Box,
    IconButton,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Divider,
    Tooltip,
    Snackbar,
    Fab,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AccessTime as AccessTimeIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Close as CloseIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import AuthService from '../Auth/AuthService';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
}));

const ActionFab = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
}));

const ShiftPolicyViewPage = () => {
    // State
    const [shiftPolicies, setShiftPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [policyToDelete, setPolicyToDelete] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        officeInTime: null,
        officeOutTime: null,
        dailyWorkingHours: '',
    });
    
    const [formErrors, setFormErrors] = useState({});

    // Fetch all shift policies
    const fetchShiftPolicies = async () => {
        setLoading(true);
        setError('');
        try {
            const rest = AuthService.pathUrl("GET", "/shift-policy");
            const response = await AuthService.getAuth(rest);
            
            if (response && response) {
                setShiftPolicies(response);
            }
        } catch (err) {
            setError('Failed to fetch shift policies. Please try again.');
            console.error('Error fetching shift policies:', err);
        } finally {
            setLoading(false);
        }
    };

    // Open add dialog
    const handleAddClick = () => {
        setDialogMode('add');
        setFormData({
            officeInTime: null,
            officeOutTime: null,
            dailyWorkingHours: '',
        });
        setFormErrors({});
        setOpenDialog(true);
    };

    // Open edit dialog
    const handleEditClick = (policy) => {
        setDialogMode('edit');
        setSelectedPolicy(policy);
        setFormData({
            officeInTime: dayjs(policy.officeInTime),
            officeOutTime: dayjs(policy.officeOutTime),
            dailyWorkingHours: policy.dailyWorkingHours || '',
            createdBy: policy.createdBy || '',
        });
        setFormErrors({});
        setOpenDialog(true);
    };

    // Handle delete
    const handleDeleteClick = (policy) => {
        setPolicyToDelete(policy);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!policyToDelete) return;
        
        setLoading(true);
        try {
            const rest = AuthService.pathUrl("DELETE", `/shift-policy/${policyToDelete.id}`);
            await AuthService.deleteAuth(rest);
            
            setSuccess('Shift policy deleted successfully');
            fetchShiftPolicies();
            setDeleteDialogOpen(false);
            setPolicyToDelete(null);
        } catch (err) {
            setError('Failed to delete shift policy. Please try again.');
            console.error('Error deleting shift policy:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleFormChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
        // Clear error for this field
        if (formErrors[field]) {
            setFormErrors({ ...formErrors, [field]: '' });
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};
        
        if (!formData.officeInTime) {
            errors.officeInTime = 'Office in time is required';
        }
        if (!formData.officeOutTime) {
            errors.officeOutTime = 'Office out time is required';
        }
        if (formData.officeInTime && formData.officeOutTime) {
            const inTime = dayjs(formData.officeInTime);
            const outTime = dayjs(formData.officeOutTime);
            if (inTime.isAfter(outTime)) {
                errors.officeOutTime = 'Office out time must be after in time';
            }
        }
        // if (!formData.createdBy || !formData.createdBy.trim()) {
        //     errors.createdBy = 'Created by is required';
        // }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Save shift policy
    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const data = {
                officeInTime: dayjs(formData.officeInTime).format('HH:mm:ss'),
                officeOutTime: dayjs(formData.officeOutTime).format('HH:mm:ss'),
            };

            let response;
            if (dialogMode === 'add') {
                const rest = AuthService.pathUrl("POST", "/shift-policy");
                response = await AuthService.postAuth(rest, data);
            } else {
                const rest = AuthService.pathUrl("PUT", `/shift-policy/${selectedPolicy.id}`);
                response = await AuthService.putAuth(rest, data);
            }

            if (response && response) {
                setSuccess(dialogMode === 'add' ? 'Shift policy created successfully' : 'Shift policy updated successfully');
                fetchShiftPolicies();
                setOpenDialog(false);
                setSelectedPolicy(null);
                setFormData({
                    officeInTime: null,
                    officeOutTime: null,
                    dailyWorkingHours: '',
                });
            }
        } catch (err) {
            setError(`Failed to ${dialogMode === 'add' ? 'create' : 'update'} shift policy. Please try again.`);
            console.error('Error saving shift policy:', err);
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchShiftPolicies();
    }, []);

    // Format time
    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            return dayjs(timeString, 'HH:mm:ss').format('hh:mm A');
        } catch {
            return timeString;
        }
    };

    // Calculate duration
    const calculateDuration = (inTime, outTime) => {
        if (!inTime || !outTime) return 'N/A';
        try {
            const start = dayjs(inTime, 'HH:mm:ss');
            const end = dayjs(outTime, 'HH:mm:ss');
            const diff = end.diff(start);
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            return `${hours}h ${minutes}m`;
        } catch {
            return 'N/A';
        }
    };

    return (
        <Container maxWidth="xl">
            <StyledPaper>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1a237e' }}>
                            Shift Policies
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Manage office shift timings and policies
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                        sx={{ height: 'fit-content' }}
                    >
                        Add Shift Policy
                    </Button>
                </Box>

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Total Policies
                                </Typography>
                                <Typography variant="h4">
                                    {shiftPolicies.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Unique Creators
                                </Typography>
                                <Typography variant="h4" color="primary.main">
                                    {new Set(shiftPolicies.map(p => p.createdBy)).size}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid> */}
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Latest Policy
                                </Typography>
                                <Typography variant="body1" color="success.main">
                                    {shiftPolicies.length > 0 ? 
                                        `ID: ${shiftPolicies[shiftPolicies.length - 1].id}` : 
                                        'N/A'
                                    }
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Refresh Button */}
                <Box display="flex" justifyContent="flex-end" mb={2}>
                    <Button
                        variant="outlined"
                        onClick={fetchShiftPolicies}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                        size="small"
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Table Section */}
                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Office In Time</strong></TableCell>
                                <TableCell><strong>Office Out Time</strong></TableCell>
                                <TableCell><strong>Working Hours</strong></TableCell>
                                <TableCell><strong>Created By</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading shift policies...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : shiftPolicies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body1" color="textSecondary">
                                            No shift policies found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                shiftPolicies.map((policy) => (
                                    <TableRow key={policy.id} hover>
                                        <TableCell>
                                            <Chip 
                                                label={`#${policy.id}`} 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <AccessTimeIcon fontSize="small" color="action" />
                                                {formatTime(policy.officeInTime)}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <AccessTimeIcon fontSize="small" color="action" />
                                                {formatTime(policy.officeOutTime)}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <ScheduleIcon fontSize="small" color="action" />
                                                {calculateDuration(policy.officeInTime, policy.officeOutTime)}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={policy.createdBy || 'N/A'} 
                                                size="small"
                                                icon={<PersonIcon />}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleEditClick(policy)}
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDeleteClick(policy)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </StyledPaper>

            {/* Add/Edit Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {dialogMode === 'add' ? 'Add New Shift Policy' : 'Edit Shift Policy'}
                        </Typography>
                        <IconButton onClick={() => setOpenDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TimePicker
                                label="Office In Time"
                                value={formData.officeInTime}
                                onChange={(newValue) => handleFormChange('officeInTime', newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!formErrors.officeInTime,
                                        helperText: formErrors.officeInTime,
                                    }
                                }}
                            />
                            <TimePicker
                                label="Office Out Time"
                                value={formData.officeOutTime}
                                onChange={(newValue) => handleFormChange('officeOutTime', newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!formErrors.officeOutTime,
                                        helperText: formErrors.officeOutTime,
                                    }
                                }}
                            />
                            {/* <TextField
                                fullWidth
                                label="Created By"
                                value={formData.createdBy}
                                onChange={(e) => handleFormChange('createdBy', e.target.value)}
                                error={!!formErrors.createdBy}
                                helperText={formErrors.createdBy}
                                placeholder="Enter creator name"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            /> */}
                            {formData.officeInTime && formData.officeOutTime && (
                                <Alert severity="info">
                                    Total Working Hours: {' '}
                                    <strong>
                                        {calculateDuration(
                                            dayjs(formData.officeInTime).format('HH:mm:ss'),
                                            dayjs(formData.officeOutTime).format('HH:mm:ss')
                                        )}
                                    </strong>
                                </Alert>
                            )}
                        </Stack>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setOpenDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {dialogMode === 'add' ? 'Create' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the shift policy with ID #{policyToDelete?.id}?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Created by: {policyToDelete?.createdBy}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDelete}
                        disabled={loading}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Floating Action Button */}
            <ActionFab
                color="primary"
                onClick={handleAddClick}
                disabled={loading}
            >
                <AddIcon />
            </ActionFab>

            <Snackbar
                open={!!success || !!error}
                autoHideDuration={6000}
                onClose={() => {
                    setSuccess('');
                    setError('');
                }}
            >
                <Alert severity={success ? 'success' : 'error'} onClose={() => {
                    setSuccess('');
                    setError('');
                }}>
                    {success || error}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ShiftPolicyViewPage;